import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Rechnungseinstellungen } from '../types';
import { rechnungseinstellungenService } from '../services/rechnungService';
import {
  Button, Card, PageHeader, LoadingScreen, Field, Input, Textarea, FormActions, DateInput,
} from '../components/ui';

/**
 * Stammdaten des Rechnungsstellers. Ohne die Pflichtangaben nach § 14 UStG lässt sich keine
 * Rechnung stellen — deshalb steht ganz oben, was noch fehlt.
 */
const RechnungEinstellungen: React.FC = () => {
  const [daten, setDaten] = useState<Rechnungseinstellungen | null>(null);
  const [form, setForm] = useState<any>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const e = await rechnungseinstellungenService.get();
      setDaten(e);
      setForm({ ...e });
    } catch { toast.error('Rechnungsdaten konnten nicht geladen werden'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const speichern = async (ev: React.FormEvent) => {
    ev.preventDefault();
    setSaving(true);
    try {
      const { fehlendePflichtangaben, ...rest } = form;
      const e = await rechnungseinstellungenService.update({
        ...rest,
        zahlungszielTage: parseInt(String(form.zahlungszielTage), 10) || 14,
        rechnungslaufTag: parseInt(String(form.rechnungslaufTag), 10) || 15,
        rechnungslaufMonat: parseInt(String(form.rechnungslaufMonat), 10) || 1,
      });
      setDaten(e);
      setForm({ ...e });
      toast.success('Rechnungsdaten gespeichert');
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Rechnungsdaten konnten nicht gespeichert werden');
    } finally { setSaving(false); }
  };

  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setForm({ ...form, [k]: e.target.value });

  if (loading) return <LoadingScreen />;

  const fehlt = daten?.fehlendePflichtangaben ?? [];

  return (
    <div className="mx-auto max-w-3xl p-6">
      <PageHeader
        title="Rechnungsdaten"
        subtitle="Absender, Zahlungsangaben und Stichtage"
      />

      {fehlt.length > 0 ? (
        <Card className="mb-4 border-amber-200 bg-amber-50 p-4">
          <p className="text-sm text-amber-900">
            <strong>Noch nicht versandfertig.</strong> Solange {fehlt.join(', ')} fehlt, lässt sich
            keine Rechnung stellen — § 14 UStG verlangt diese Angaben auf jeder Rechnung.
          </p>
        </Card>
      ) : (
        <Card className="mb-4 border-green-200 bg-green-50 p-4">
          <p className="text-sm text-green-900">Alle Pflichtangaben sind hinterlegt.</p>
        </Card>
      )}

      <form onSubmit={speichern} className="space-y-6">
        <Card className="p-6">
          <h2 className="mb-4 text-base font-semibold text-gray-900">Absender</h2>
          <div className="space-y-4">
            <Field label="Firmenname" required><Input value={form.firmenname ?? ''} onChange={set('firmenname')} /></Field>
            <Field label="Zusatz"><Input value={form.zusatz ?? ''} onChange={set('zusatz')} /></Field>
            <Field label="Straße und Hausnummer" required><Input value={form.strasse ?? ''} onChange={set('strasse')} /></Field>
            <div className="grid grid-cols-3 gap-4">
              <Field label="PLZ" required><Input value={form.plz ?? ''} onChange={set('plz')} /></Field>
              <div className="col-span-2">
                <Field label="Ort" required><Input value={form.ort ?? ''} onChange={set('ort')} /></Field>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="USt-IdNr" hint="USt-IdNr oder Steuernummer genügt">
                <Input value={form.ustId ?? ''} onChange={set('ustId')} />
              </Field>
              <Field label="Steuernummer"><Input value={form.steuernummer ?? ''} onChange={set('steuernummer')} /></Field>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Telefon"><Input value={form.telefon ?? ''} onChange={set('telefon')} /></Field>
              <Field label="Mobil"><Input value={form.mobil ?? ''} onChange={set('mobil')} /></Field>
            </div>
            <Field label="E-Mail"><Input type="email" value={form.email ?? ''} onChange={set('email')} /></Field>
            <Field label="Geschäftsführer" hint="Steht im Fuß der Rechnung, z. B. „Horst Rechter, Ruby Rechter“">
              <Input value={form.geschaeftsfuehrer ?? ''} onChange={set('geschaeftsfuehrer')} />
            </Field>
          </div>
        </Card>

        <Card className="p-6">
          <h2 className="mb-4 text-base font-semibold text-gray-900">Zahlung</h2>
          <div className="space-y-4">
            <Field label="Bank"><Input value={form.bank ?? ''} onChange={set('bank')} /></Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="IBAN"><Input value={form.iban ?? ''} onChange={set('iban')} /></Field>
              <Field label="BIC"><Input value={form.bic ?? ''} onChange={set('bic')} /></Field>
            </div>
            <Field label="Zweite Bank" hint="Optional, erscheint als weitere Zeile im Fuß">
              <Input value={form.bank2 ?? ''} onChange={set('bank2')} />
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="IBAN"><Input value={form.iban2 ?? ''} onChange={set('iban2')} /></Field>
              <Field label="BIC"><Input value={form.bic2 ?? ''} onChange={set('bic2')} /></Field>
            </div>
            <Field label="Zahlungsziel (Tage)" required>
              <Input type="number" min={0} max={365} value={form.zahlungszielTage ?? 14} onChange={set('zahlungszielTage')} />
            </Field>
          </div>
        </Card>

        <Card className="p-6">
          <h2 className="mb-1 text-base font-semibold text-gray-900">Abrechnung</h2>
          <p className="mb-4 text-sm text-gray-600">
            Jeder Vertrag wird einmal im Jahr zum Jahrestag der Unterschrift abgerechnet, im Voraus für das
            kommende Vertragsjahr. Die Entwürfe entstehen automatisch jeden Morgen.
          </p>
          <div className="space-y-4">
            <Field
              label="Abrechnung ab"
              required
              hint="Vertragsjahre, die davor beginnen, gelten als abgerechnet (Altbestand aus der Lexware-Zeit). Ohne diesen Stichtag entstehen keine Rechnungen."
            >
              <DateInput value={(form.abrechnungAb ?? '').split('T')[0]} onChange={v => setForm({ ...form, abrechnungAb: v })} />
            </Field>
            <Field label="Präfix der Rechnungsnummer" hint="Ergibt z. B. R-2026-0001">
              <Input maxLength={10} value={form.rechnungsnummerPraefix ?? 'R'} onChange={set('rechnungsnummerPraefix')} />
            </Field>
          </div>
        </Card>

        <Card className="p-6">
          <h2 className="mb-4 text-base font-semibold text-gray-900">Texte auf der Rechnung</h2>
          <div className="space-y-4">
            <Field label="Hinweis unter den Summen" hint="Die erste Zeile wird fett gedruckt, z. B. der Dank für den Auftrag">
              <Textarea rows={4} value={form.fusszeile ?? ''} onChange={set('fusszeile')} />
            </Field>
            <Field label="Weitere Zeilen im Fuß" hint="Je Zeile eine Fußzeile, z. B. Sitz, Handelsregister, Webseite">
              <Textarea rows={3} value={form.fussZusatz ?? ''} onChange={set('fussZusatz')} />
            </Field>
          </div>
        </Card>

        <FormActions>
          <Button type="submit" loading={saving}>Speichern</Button>
        </FormActions>
      </form>
    </div>
  );
};

export default RechnungEinstellungen;
